/**
 * PlatformLabPage — 플랫폼 랩 (/platform-lab)
 * 5탭 SPA: API 버전(C025) / 시퀀스·ID(C023) / 비동기(C019) / 복원력(C018) / 분산 세션(C036)
 *
 * 5상태:
 *   - default:  탭 선택 + 결과 영역 (각 탭 내부)
 *   - loading:  탭 내 API 호출 중 Spinner
 *   - empty:    탭 내 첫 진입 EmptyState
 *   - error:    탭 내 ErrorState 카드
 *   - dialog:   v1 Deprecation Callout / 세션 ID 전체보기 경고 (inline, non-modal)
 *
 * 권한: AUTHENTICATED (ROLE_USER+) — 서버 @HasPermission 이 이중 방어
 * 모바일: md(768px) 이상 단일 열 유지. 탭은 가로 스크롤.
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@ui-kit';
import { VersioningTab } from '../components/VersioningTab';
import { IdGenTab } from '../components/IdGenTab';
import { AsyncTab } from '../components/AsyncTab';
import { ResilienceTab } from '../components/ResilienceTab';
import { SessionTab } from '../components/SessionTab';
import { PlatformLabCommonUsagePanel } from '../components/PlatformLabCommonUsagePanel';

type TabId = 'versioning' | 'idgen' | 'async' | 'resilience' | 'session';

const TABS: { id: TabId; labelKey: string }[] = [
  { id: 'versioning', labelKey: 'platformLab.tab.versioning' },
  { id: 'idgen', labelKey: 'platformLab.tab.idgen' },
  { id: 'async', labelKey: 'platformLab.tab.async' },
  { id: 'resilience', labelKey: 'platformLab.tab.resilience' },
  { id: 'session', labelKey: 'platformLab.tab.session' },
];

export function PlatformLabPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabId>('versioning');

  // Tab 전환 시 포커스 관리 (접근성: Tab 키 → 탭 내 첫 인터랙티브 요소)
  useEffect(() => {
    const el = document.getElementById(`platform-lab-tab-panel-${activeTab}`);
    if (el) {
      const first = el.querySelector<HTMLElement>('button, input, select, a[href]');
      first?.focus();
    }
  }, [activeTab]);

  // Keyboard: 탭 목록에서 방향키 이동
  const handleTabKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, currentIdx: number) => {
    if (e.key === 'ArrowRight') {
      const next = TABS[(currentIdx + 1) % TABS.length];
      setActiveTab(next.id);
      document.getElementById(`platform-lab-tab-btn-${next.id}`)?.focus();
    } else if (e.key === 'ArrowLeft') {
      const prev = TABS[(currentIdx - 1 + TABS.length) % TABS.length];
      setActiveTab(prev.id);
      document.getElementById(`platform-lab-tab-btn-${prev.id}`)?.focus();
    }
  };

  return (
    <div style={{ padding: 0 }}>
      {/* 페이지 헤더 */}
      <PageHeader
        title={t('platformLab.page.title')}
        breadcrumb={
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
            {t('platformLab.page.subtitle')}
          </span>
        }
      />

      {/* 탭 바 */}
      <div
        role="tablist"
        aria-label={t('platformLab.page.title')}
        style={{
          display: 'flex',
          gap: 0,
          borderBottom: '2px solid var(--color-surface-border)',
          marginBottom: 'var(--spacing-5)',
          overflowX: 'auto',
        }}
      >
        {TABS.map((tab, idx) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              id={`platform-lab-tab-btn-${tab.id}`}
              role="tab"
              aria-selected={isActive}
              aria-controls={`platform-lab-tab-panel-${tab.id}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setActiveTab(tab.id)}
              onKeyDown={(e) => handleTabKeyDown(e, idx)}
              style={{
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'transparent',
                border: 'none',
                borderBottom: isActive
                  ? '2px solid var(--color-brand-primary)'
                  : '2px solid transparent',
                marginBottom: -2,
                fontSize: 'var(--font-size-sm)',
                fontWeight: isActive ? 700 : 500,
                color: isActive ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
                whiteSpace: 'nowrap',
                transition: 'color 0.15s, border-color 0.15s',
              }}
            >
              {t(tab.labelKey)}
            </button>
          );
        })}
      </div>

      {/* 탭 패널들 */}
      {TABS.map((tab) => (
        <div
          key={tab.id}
          id={`platform-lab-tab-panel-${tab.id}`}
          role="tabpanel"
          aria-labelledby={`platform-lab-tab-btn-${tab.id}`}
          tabIndex={0}
          hidden={activeTab !== tab.id}
          style={{
            outline: 'none',
            minHeight: 300,
          }}
        >
          {tab.id === 'versioning' && <VersioningTab />}
          {tab.id === 'idgen' && <IdGenTab />}
          {tab.id === 'async' && <AsyncTab />}
          {tab.id === 'resilience' && <ResilienceTab />}
          {tab.id === 'session' && <SessionTab />}
        </div>
      ))}

      {/* 공통 모듈 시연 패널 */}
      <PlatformLabCommonUsagePanel />

      {/* 반응형 */}
      <style>{`
        @media (max-width: 768px) {
          [role="tablist"] { gap: 0; }
        }
      `}</style>
    </div>
  );
}
